import type { BodensteckbriefEntry } from './listen.service';

/**
 * Namensmatrix: Kreuztabelle der Feinbodenform-Codes.
 *
 * Ein FBF-Code setzt sich aus zwei Teilen zusammen:
 *   <Lokalnamen-Kuerzel><Substrat-Suffix>   z.B. "Mz" + "LG" = "MzLG".
 *
 * Die Matrix stellt diese beiden Teile gegeneinander:
 *   - Zeilen (y): Lokalnamen-Kuerzel (erster Teil), z.B. "Mz".
 *   - Spalten (x): Substrat-Suffix (zweiter Teil, = `fbf_klassensuffix` des
 *     Steckbriefs), z.B. "LG".
 *
 * Wichtig: Dasselbe Kuerzel steht je nach Substrat fuer VERSCHIEDENE volle
 * Lokalnamen ("Mz" = Melzower bei "S", Moeglenzer bei "LG", ...). Eine einzelne
 * Zelle (Kuerzel x Suffix) ist jedoch eindeutig: genau ein voller Lokalname und
 * genau eine HBF-Seite (Steckbrief). Deshalb traegt jede Treffer-Zelle ihren
 * eigenen Lokalnamen und die Steckbrief-ID, auf die der Klick fuehrt.
 *
 * Reines Modul ohne Angular-/DB-Abhaengigkeiten (nur `import type`), damit es
 * isoliert unter vitest testbar ist (vgl. feinbodenform-derivation.ts).
 */

const trim = (s: unknown): string => (s == null ? '' : String(s)).trim();

/** Eine Treffer-Zelle = eindeutige Kombination Kuerzel x Substrat-Suffix. */
export interface NamensmatrixCell {
  /** Steckbrief-ID (HBF-Seite), auf die der Klick fuehrt. */
  steckbriefId: string;
  /** Voller Lokalname dieser FBF (variiert je Substrat-Spalte). */
  lokalname: string;
  /** FBF ist im Katalog als gestrichen markiert. */
  gestrichen: boolean;
}

/** Eine Matrix-Zeile = ein Lokalnamen-Kuerzel mit seinen Substrat-Treffern. */
export interface NamensmatrixRow {
  /** Lokalnamen-Kuerzel (erster Teil der FBF), z.B. "Mz". */
  kuerzel: string;
  /** Treffer je Substrat-Suffix (Suffix -> Zelle). */
  cells: Map<string, NamensmatrixCell>;
  /** Alle vollen Lokalnamen dieser Zeile (distinct) – fuer Suche/Tooltip. */
  namen: string[];
  /** Anzahl belegter Substrat-Spalten (= cells.size) – fuer Sortierung. */
  anzahl: number;
}

/** Mehrdeutige Zelle: dieselbe (Kuerzel,Suffix)-Kombination in mehreren
 *  Steckbriefen (mehrdeutiger FBF-Code). In der aktuellen Katalog-Daten leer. */
export interface NamensmatrixDuplicate {
  kuerzel: string;
  suffix: string;
  steckbriefe: string[];
}

export interface Namensmatrix {
  /** Substrat-Suffixe (Spalten), alphabetisch (deutsch) sortiert. */
  cols: string[];
  /** Zeilen, nach Kuerzel (deutsch) sortiert. */
  rows: NamensmatrixRow[];
  /** Mehrdeutige Zellen (erster Treffer gewinnt; hier nur protokolliert). */
  duplicates: NamensmatrixDuplicate[];
}

/**
 * Baut die Namensmatrix aus dem Hauptbodenformenkatalog.
 *
 * - Eine FBF ohne Abkuerzung bildet kein Kuerzel und wird uebersprungen
 *   (Parser-Kopfzeilen "Abkuerzung" bzw. abkuerzungslose Querverweise).
 * - Bei mehrdeutigen Zellen gewinnt der erste Treffer (deterministisch, in
 *   Katalog-Reihenfolge); die Kollision wird in `duplicates` gemeldet.
 */
export function buildNamensmatrix(catalog: BodensteckbriefEntry[]): Namensmatrix {
  const rowMap = new Map<string, NamensmatrixRow>();
  const colSet = new Set<string>();
  const dupeMap = new Map<string, string[]>(); // "kuerzel|suffix" -> steckbrief-ids

  for (const s of catalog || []) {
    const suffix = trim(s.fbf_klassensuffix);
    for (const f of s.feinbodenformen || []) {
      const kuerzel = trim(f.abkuerzung);
      if (!kuerzel) continue;
      colSet.add(suffix);

      let row = rowMap.get(kuerzel);
      if (!row) {
        row = { kuerzel, cells: new Map(), namen: [], anzahl: 0 };
        rowMap.set(kuerzel, row);
      }

      const lokalname = trim(f.lokalname);
      if (lokalname && !row.namen.includes(lokalname)) row.namen.push(lokalname);

      if (row.cells.has(suffix)) {
        // Mehrdeutig: ersten Treffer behalten, Kollision protokollieren.
        const key = kuerzel + '|' + suffix;
        const first = row.cells.get(suffix)!;
        const list = dupeMap.get(key) || [first.steckbriefId];
        list.push(trim(s.id));
        dupeMap.set(key, list);
      } else {
        row.cells.set(suffix, { steckbriefId: trim(s.id), lokalname, gestrichen: !!f.gestrichen });
      }
    }
  }

  for (const row of rowMap.values()) {
    row.anzahl = row.cells.size;
    row.namen.sort((a, b) => a.localeCompare(b, 'de'));
  }

  const cols = [...colSet].sort((a, b) => a.localeCompare(b, 'de'));
  const rows = [...rowMap.values()].sort((a, b) => a.kuerzel.localeCompare(b.kuerzel, 'de'));
  const duplicates: NamensmatrixDuplicate[] = [...dupeMap.entries()].map(([k, steckbriefe]) => {
    const sep = k.indexOf('|');
    return { kuerzel: k.slice(0, sep), suffix: k.slice(sep + 1), steckbriefe };
  });

  return { cols, rows, duplicates };
}
